import { useState } from "react";
import { Link } from "react-router-dom";
import jsPDF from "jspdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Download, FileText } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_BODY = `Every day, single mothers and seniors in Baytown fall through the cracks of a fragmented social service system — not because local resources don't exist, but because navigating them requires an air traffic controller.

Having managed a 105-family service-connector caseload at The Woodlawn Organization / WCDC in Chicago from 2001 to 2007, I am proposing a localized, multigenerational adaptation built specifically for the dual-county landscape of Baytown. I've built a conceptual overview at baytownbridge.org showing how we can leverage existing assets like Lee College, the Baytown Housing Authority, Harris Health, and the City's CDBG public-service set-aside to launch this smoothly — and directly advance all five objectives in the City of Baytown's adopted 2025–2029 Consolidated Plan.

I would appreciate a 30-minute briefing at your convenience.`;

export default function PitchLetterPage() {
  const [form, setForm] = useState({
    recipient_name: "",
    recipient_title: "",
    recipient_org: "City of Baytown",
    salutation: "Dear",
    body: DEFAULT_BODY,
    sender_name: "George Broughton",
    sender_title: "Program Creator & Founding Architect · CEO, Sterile Mate LLC",
    sender_email: "george.broughton@sterilemate.com",
  });
  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const generatePDF = () => {
    if (!form.recipient_name) {
      toast.error("Recipient name is required.");
      return;
    }
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const navy = [10, 25, 47];
    const red = [139, 0, 0];
    const slate = [51, 65, 85];
    const margin = 64;
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    let y = margin;

    // Letterhead
    doc.setFillColor(...navy);
    doc.rect(0, 0, pageW, 90, "F");
    doc.setTextColor(212, 175, 55);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("THE BAYTOWN BRIDGE", margin, 38);
    doc.setTextColor(249, 247, 241);
    doc.setFont("times", "bold");
    doc.setFontSize(22);
    doc.text("A Multigenerational Service Connector Initiative", margin, 64);

    y = 130;

    // Date
    doc.setTextColor(...slate);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const today = new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    doc.text(today, margin, y);
    y += 30;

    // Recipient block
    doc.setTextColor(...navy);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(form.recipient_name, margin, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    if (form.recipient_title) {
      doc.text(form.recipient_title, margin, y);
      y += 14;
    }
    if (form.recipient_org) {
      doc.text(form.recipient_org, margin, y);
      y += 14;
    }
    y += 22;

    // Salutation
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    const sal = `${form.salutation} ${form.recipient_name.split(" ").slice(-1)[0]},`;
    doc.text(sal, margin, y);
    y += 22;

    // Body
    doc.setTextColor(...slate);
    doc.setFontSize(11);
    const paragraphs = form.body.split(/\n\n+/);
    paragraphs.forEach((p) => {
      const lines = doc.splitTextToSize(p.trim(), pageW - margin * 2);
      lines.forEach((line) => {
        if (y > pageH - margin - 80) {
          doc.addPage();
          y = margin;
        }
        doc.text(line, margin, y);
        y += 15;
      });
      y += 10;
    });

    // Signature
    if (y > pageH - 160) {
      doc.addPage();
      y = margin;
    }
    y += 14;
    doc.text("Respectfully,", margin, y);
    y += 50;

    doc.setDrawColor(...red);
    doc.setLineWidth(1);
    doc.line(margin, y, margin + 110, y);
    y += 18;

    doc.setTextColor(...navy);
    doc.setFont("helvetica", "bold");
    doc.text(form.sender_name, margin, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...slate);
    doc.text(form.sender_title, margin, y);
    y += 14;
    doc.text("Service Connector · The Woodlawn Organization / WCDC, Chicago (2001–2007)", margin, y);
    y += 14;
    doc.text(form.sender_email, margin, y);

    const file = `pitch-letter-${form.recipient_name.replace(/\s+/g, "-").toLowerCase()}.pdf`;
    doc.save(file);
    toast.success("Pitch letter PDF generated.");
  };

  return (
    <main data-testid="pitch-page" className="bg-[#F9F7F1] min-h-screen">
      <div className="bg-[#0A192F] text-[#F9F7F1]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="pitch-back-link"
            className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] hover:text-[#F9F7F1] flex items-center gap-2"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Baytown Bridge
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#CBD5E1]">
            Pitch Letter Generator
          </span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#8B0000]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
                Auto-Filler · Council Member Outreach
              </span>
            </div>
            <h1 className="font-display text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Generate a personalized pitch letter.
            </h1>
            <p className="mt-4 text-[#334155] leading-relaxed max-w-2xl">
              Fill in the recipient's name and role. We'll produce a formatted, signed
              PDF letter ready to attach to an introductory email — pre-loaded with the
              Baytown Bridge value proposition and your founder credentials.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Form */}
          <form
            data-testid="pitch-form"
            onSubmit={(e) => {
              e.preventDefault();
              generatePDF();
            }}
            className="lg:col-span-7 bg-white border border-[#CBD5E1] p-7 lg:p-9 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Recipient Full Name" required>
                <Input
                  data-testid="pitch-input-recipient"
                  value={form.recipient_name}
                  onChange={update("recipient_name")}
                  placeholder="The Honorable Brandon Capetillo"
                  className="rounded-none border-[#CBD5E1] h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
                />
              </Field>
              <Field label="Title">
                <Input
                  data-testid="pitch-input-title"
                  value={form.recipient_title}
                  onChange={update("recipient_title")}
                  placeholder="Mayor / Council Member, District X"
                  className="rounded-none border-[#CBD5E1] h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
                />
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Organization">
                <Input
                  data-testid="pitch-input-org"
                  value={form.recipient_org}
                  onChange={update("recipient_org")}
                  className="rounded-none border-[#CBD5E1] h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
                />
              </Field>
              <Field label="Salutation">
                <Input
                  data-testid="pitch-input-salutation"
                  value={form.salutation}
                  onChange={update("salutation")}
                  placeholder="Dear / Honorable"
                  className="rounded-none border-[#CBD5E1] h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
                />
              </Field>
            </div>

            <Field label="Letter Body (edit freely)">
              <Textarea
                data-testid="pitch-input-body"
                value={form.body}
                onChange={update("body")}
                rows={12}
                className="rounded-none border-[#CBD5E1] focus-visible:ring-0 focus-visible:border-[#0A192F] font-mono text-sm leading-relaxed"
              />
            </Field>

            <div className="grid sm:grid-cols-2 gap-5 pt-3 border-t border-[#CBD5E1]">
              <Field label="Your Name">
                <Input
                  data-testid="pitch-input-sender"
                  value={form.sender_name}
                  onChange={update("sender_name")}
                  className="rounded-none border-[#CBD5E1] h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
                />
              </Field>
              <Field label="Your Email">
                <Input
                  data-testid="pitch-input-email"
                  value={form.sender_email}
                  onChange={update("sender_email")}
                  className="rounded-none border-[#CBD5E1] h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
                />
              </Field>
            </div>

            <Button
              type="submit"
              data-testid="pitch-generate-btn"
              className="w-full rounded-none bg-[#0A192F] hover:bg-[#15243F] text-[#F9F7F1] h-12 font-mono text-xs uppercase tracking-widest"
            >
              <Download className="h-4 w-4 mr-2" /> Generate & Download PDF
            </Button>
          </form>

          {/* Preview */}
          <aside
            data-testid="pitch-preview"
            className="lg:col-span-5 bg-white border border-[#CBD5E1] p-8 lg:p-10 self-start sticky top-24 max-h-[80vh] overflow-y-auto"
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-4 flex items-center gap-2">
              <FileText className="h-3.5 w-3.5" /> Live Preview
            </div>
            <div className="border-t-4 border-[#0A192F] pt-4 text-sm text-[#334155]">
              <div className="font-mono text-[9px] uppercase tracking-widest text-[#8B0000]">
                The Baytown Bridge
              </div>
              <div className="font-display text-base text-[#0A192F] mb-6">
                A Multigenerational Service Connector Initiative
              </div>

              <div className="text-[#475569] text-xs mb-4">
                {new Date().toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>

              <div className="mb-4 leading-snug">
                <div className="font-semibold text-[#0A192F]">
                  {form.recipient_name || "[Recipient Name]"}
                </div>
                {form.recipient_title && <div>{form.recipient_title}</div>}
                {form.recipient_org && <div>{form.recipient_org}</div>}
              </div>

              <div className="mb-4">
                {form.salutation} {(form.recipient_name || "[Last]").split(" ").slice(-1)[0]},
              </div>

              <div className="leading-relaxed whitespace-pre-wrap text-xs">{form.body}</div>

              <div className="mt-6">Respectfully,</div>
              <div className="mt-8 pt-1 border-t border-[#8B0000] inline-block w-32" />
              <div className="mt-2 font-semibold text-[#0A192F]">{form.sender_name}</div>
              <div className="text-xs">{form.sender_title}</div>
              <div className="text-xs">Service Connector · The Woodlawn Organization / WCDC, Chicago (2001–2007)</div>
              <div className="text-xs">{form.sender_email}</div>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
        {label}
        {required && <span className="text-[#8B0000] ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
